import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../components/ui/table';
import AgingBadge from '../components/AgingBadge';
import { ArrowLeft, Users } from 'lucide-react';

const STATUS_LABELS = {
  pending_approval: 'Pending Approval',
  approved_not_started: 'Approved / Not Started',
  job_posting: 'Job Posting',
  interview_scheduled: 'Interview Scheduled',
  waiting_interview_result: 'Waiting for Result',
  offer_sent: 'Offer Sent',
  accepted_waiting_start: 'Accepted / Waiting',
  started: 'Started',
  cancelled_on_hold: 'Cancelled',
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [headcount, setHeadcount] = useState([]);
  const [cases, setCases] = useState([]);
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const api = authAxios();
        const [projRes, reqRes, actRes, casesRes, posRes] = await Promise.all([
          api.get(`/projects/${projectId}`),
          api.get('/headcount/required', { params: { project_id: projectId } }),
          api.get('/headcount/actual', { params: { project_id: projectId } }),
          api.get('/recruitment-cases', { params: { project_id: projectId } }),
          api.get('/positions'),
        ]);

        setProject(projRes.data);
        const posMap = {};
        posRes.data.forEach(p => { posMap[p.id] = p.name; });
        setPositions(posMap);

        const actMap = {};
        actRes.data.forEach(a => { actMap[a.position_id] = a.actual_qty; });

        const hc = reqRes.data.map(r => ({
          position_id: r.position_id,
          position_name: posMap[r.position_id] || 'Unknown',
          required: r.required_qty,
          actual: actMap[r.position_id] || 0,
          gap: Math.max(r.required_qty - (actMap[r.position_id] || 0), 0),
        }));
        setHeadcount(hc);

        const openCases = casesRes.data.filter(c => !['started', 'cancelled_on_hold'].includes(c.status));
        setCases(openCases);
      } catch {
        toast.error('Failed to load project details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500" data-testid="project-detail-loading">Loading...</div>;
  }

  const totalReq = headcount.reduce((s, h) => s + h.required, 0);
  const totalAct = headcount.reduce((s, h) => s + h.actual, 0);
  const totalGap = headcount.reduce((s, h) => s + h.gap, 0);

  return (
    <div data-testid="project-detail-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-to-projects">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{project?.name}</h1>
          <p className="text-sm text-slate-500">{project?.area} &middot; {project?.description}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Required</p>
            <p className="text-3xl font-bold text-slate-900">{totalReq}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Actual</p>
            <p className="text-3xl font-bold text-slate-900">{totalAct}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Gap</p>
            <p className={`text-3xl font-bold ${totalGap > 0 ? 'text-red-600' : 'text-green-600'}`}>{totalGap}</p>
          </CardContent>
        </Card>
      </div>

      {/* Headcount by Position */}
      <Card className="border-slate-200" data-testid="headcount-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Users className="h-4 w-4" /> Headcount by Position
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4">Position</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Required</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Actual</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Gap</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {headcount.map(h => (
                <TableRow key={h.position_id} className="border-b border-slate-100">
                  <TableCell className="text-sm font-medium text-slate-800 px-4">{h.position_name}</TableCell>
                  <TableCell className="text-sm text-slate-600 text-center font-mono">{h.required}</TableCell>
                  <TableCell className="text-sm text-slate-600 text-center font-mono">{h.actual}</TableCell>
                  <TableCell className="text-center">
                    {h.gap > 0 ? (
                      <Badge className="bg-red-50 text-red-700 border-red-200 font-mono">{h.gap}</Badge>
                    ) : (
                      <Badge className="bg-green-50 text-green-700 border-green-200 font-mono">0</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Open Recruitment Cases */}
      <Card className="border-slate-200" data-testid="project-cases-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-800">
            Open Recruitment Cases ({cases.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {cases.length === 0 ? (
            <p className="text-sm text-slate-400 p-4 text-center">No open recruitment cases</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 border-b border-slate-200">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4">Position</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Qty</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Aging</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cases.map(c => (
                  <TableRow key={c.id} className="border-b border-slate-100">
                    <TableCell className="text-sm font-medium text-slate-800 px-4">{positions[c.position_id] || 'Unknown'}</TableCell>
                    <TableCell className="text-sm text-slate-600 text-center font-mono">{c.qty}</TableCell>
                    <TableCell className="text-sm text-slate-700">{STATUS_LABELS[c.status] || c.status}</TableCell>
                    <TableCell>
                      <AgingBadge days={c.aging_days} group={c.aging_group} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
